import { formatDate, formatTime } from "@/shared/lib/date-time";
import { Badge, Card, CardContent, CardHeader, CardTitle } from "@devhop/ui";
import {
  CalendarIcon,
  ClockIcon,
  MapPinIcon,
  UserIcon,
  UsersIcon,
} from "lucide-react";
import type { ClassSessionDTO } from "../../domain/contracts/sessions";
import { formatSessionPlace } from "./session-display";
import { SessionInstructorRoleBadges } from "./SessionInstructorRoleBadges";

type ClassSessionGeneralInfoProps = {
  session: ClassSessionDTO;
};

export function ClassSessionGeneralInfo({
  session,
}: ClassSessionGeneralInfoProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>ຂໍ້ມູນທົ່ວໄປ</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {/* Left Column */}
          <div className="space-y-4">
            <div>
              <p className="text-muted-foreground text-xs uppercase tracking-wide">
                Template
              </p>
              <p className="mt-0.5 font-semibold text-sm">
                {session.templateName}
              </p>
            </div>

            <div>
              <p className="text-muted-foreground text-xs uppercase tracking-wide">
                ສະຖານທີ່
              </p>
              <div className="mt-0.5 flex items-center gap-2">
                <MapPinIcon className="size-4 text-muted-foreground" />
                <p className="font-semibold text-sm">
                  {formatSessionPlace(session)}
                </p>
              </div>
            </div>

            <div>
              <p className="text-muted-foreground text-xs uppercase tracking-wide">
                ປະເພດໂຄງການ
              </p>
              <div className="mt-0.5 flex flex-wrap items-center gap-2">
                {session.engagementKind === "partner_instructor_only" ? (
                  <Badge variant="outline" className="border-foreground/30">
                    Partner (instructor-only)
                  </Badge>
                ) : (
                  <span className="font-semibold text-sm">ມາດຕະຖານ</span>
                )}
              </div>
            </div>

            <div>
              <p className="text-muted-foreground text-xs uppercase tracking-wide">
                ຄວາມຈຸ
              </p>
              <div className="mt-0.5 flex items-center gap-2">
                <UsersIcon className="size-4 text-muted-foreground" />
                <p className="font-semibold text-sm">{session.capacity} ຄົນ</p>
              </div>
            </div>

            {session.notes && (
              <div>
                <p className="text-muted-foreground text-xs uppercase tracking-wide">
                  ໝາຍເຫດ
                </p>
                <p className="mt-0.5 text-sm">{session.notes}</p>
              </div>
            )}
          </div>

          {/* Right Column */}
          <div className="space-y-4">
            <div>
              <p className="text-muted-foreground text-xs uppercase tracking-wide">
                ວັນທີ
              </p>
              <div className="mt-0.5 flex items-center gap-2">
                <CalendarIcon className="size-4 text-muted-foreground" />
                <p className="font-semibold text-sm">
                  {formatDate(session.date)}
                </p>
              </div>
            </div>

            <div>
              <p className="text-muted-foreground text-xs uppercase tracking-wide">
                ເວລາ
              </p>
              <div className="mt-0.5 flex items-center gap-2">
                <ClockIcon className="size-4 text-muted-foreground" />
                <p className="font-semibold text-sm">
                  {formatTime(session.startTime)} -{" "}
                  {formatTime(session.endTime)}
                </p>
              </div>
            </div>

            <div>
              <p className="text-muted-foreground text-xs uppercase tracking-wide">
                ຄູຜູ້ສອນ
              </p>
              <div className="mt-0.5 flex items-start gap-2">
                <UserIcon className="mt-0.5 size-4 text-muted-foreground" />
                <div className="space-y-1.5">
                  {session.instructors?.length ? (
                    session.instructors.map((inst) => (
                      <div
                        key={`${inst.instructorId}-${inst.role}`}
                        className="flex flex-wrap items-center gap-1.5"
                      >
                        <p className="font-semibold text-sm">
                          {inst.name ||
                            session.instructorNames[
                              session.instructorIds.indexOf(inst.instructorId)
                            ] ||
                            inst.instructorId}
                        </p>
                        <SessionInstructorRoleBadges role={inst.role} />
                      </div>
                    ))
                  ) : Array.isArray(session.instructorNames) &&
                    session.instructorNames.length > 0 ? (
                    <p className="font-semibold text-sm">
                      {session.instructorNames.join(", ")}
                    </p>
                  ) : (
                    <p className="font-semibold text-sm">-</p>
                  )}
                </div>
              </div>
            </div>

            <div>
              <p className="text-muted-foreground text-xs uppercase tracking-wide">
                ສະຖານະ
              </p>
              <p className="mt-0.5 font-semibold text-sm">
                {session.status === "scheduled"
                  ? "ຈັດຕາຕະລາງແລ້ວ"
                  : session.status === "completed"
                    ? "ເຂົ້າຮຽນແລ້ວ"
                    : "ຍົກເລີກ"}
              </p>
            </div>
          </div>
        </div>

        {/* Metadata Section */}
        <div className="mt-6 border-border border-t pt-4">
          <div className="grid grid-cols-1 gap-4 text-muted-foreground text-xs md:grid-cols-3">
            <div>
              <p className="uppercase tracking-wide">ວັນທີສ້າງ</p>
              <p className="mt-1 font-semibold text-foreground">
                {formatDate(session.createdAt)}
              </p>
            </div>
            {session.createdBy && (
              <div>
                <p className="uppercase tracking-wide">ຜູ້ສ້າງ</p>
                <p className="mt-1 font-semibold text-foreground">
                  {session.createdByUserName ?? session.createdBy}
                </p>
              </div>
            )}
            <div>
              <p className="uppercase tracking-wide">ວັນທີອັບເດດລ່າສຸດ</p>
              <p className="mt-1 font-semibold text-foreground">
                {formatDate(session.updatedAt)}
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
